import React from 'react';
import { Flex, Box, Image, HStack, Text } from '@chakra-ui/react';
import { useLocation, useMatch } from 'react-router-dom';
import PagerDutyLogo from '@/assets/images/pagerduty-black-rgb.svg';
import { paths } from '@/config/paths';
import { useAgentById } from '@/hooks/use-agents';
import { LAYOUT_REGISTRY } from '@/config/layout-config';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { useAppSelector } from '@/stores';
import { selectUser } from '@/stores/selectors/user-selectors';
import { getSubdomainContext } from '@/config/subdomain-config';
import { NavLink } from './nav-link';
import { UserMenu } from './user-menu';

const subdomainContext = getSubdomainContext();

export const AppHeader: React.FC = () => {
  const location = useLocation();
  const agentMatch = useMatch(paths.AssistantAgent);
  // When on a non-agent page (e.g. /rule-summary), agentMatch is null.
  // Fall back to the Redux selectedAgent so nav items stay visible.
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const agentId = agentMatch?.params?.agentId ?? selectedAgent?.id ?? undefined;
  // Read agent from DB-backed catalogue (loaded once at app root).
  const agent = useAgentById(agentId);
  // Fall back to `generic` when the agent exists in the catalogue but has
  // no LAYOUT_REGISTRY entry — e.g. an agent created in DB without a
  // hardcoded FE layout. The catch-all keeps the header from crashing.
  const layout =
    (agent && LAYOUT_REGISTRY[agent.id]) ?? LAYOUT_REGISTRY.generic;
  const user = useAppSelector(selectUser);

  const agentChatPath = agentId
    ? `${paths.Assistant}/${agentId}`
    : paths.Assistant;

  return (
    <Flex
      as="header"
      position="sticky"
      top={0}
      zIndex={10}
      bg="gray.50"
      borderBottom="1px"
      borderColor="gray.200"
      maxH="55px"
      px={4}
      py={4}
      align="center"
      justify="space-between"
    >
      {/* Logo - Left */}
      <Box mr={5}>
        <Image src={PagerDutyLogo} alt="PagerDuty" h="25px" w="auto" />
      </Box>

      {/* Navigation Links - Center */}
      <HStack spacing={0} justifyContent="start" w="100%">
        {/* Fixed items: Home (agent selector) and Assistant (current agent chat) */}
        {subdomainContext.type !== 'agent' && (
          <NavLink
            to={paths.Assistant}
            isActive={location.pathname === paths.Assistant}
          >
            Home
          </NavLink>
        )}
        {agentId && (
          <NavLink
            to={agentChatPath}
            isActive={
              location.pathname === agentChatPath ||
              location.pathname.startsWith(agentChatPath + '/')
            }
          >
            Assistant
          </NavLink>
        )}

        {/* Agent-specific items (Best Practices, Rule Summary, etc.) */}
        {layout.navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            isActive={
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + '/')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </HStack>

      {/* Subdomain + User Menu - Right */}
      <HStack spacing={3} flexShrink={0}>
        {user?.subdomain && (
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
            {user.subdomain}
          </Text>
        )}
        <UserMenu />
      </HStack>
    </Flex>
  );
};

export default AppHeader;
